import React, { useCallback, useEffect, useState } from 'react';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import SkeletonBlock from '../ui/SkeletonBlock';
import StatusBanner from '../ui/StatusBanner';
import FocusQueueRow from './FocusQueueRow';
import {
  dismissFocusQueueItem,
  listFocusQueue
} from '../../services/focusQueueApi';
import {
  applyFocusQueueCountDelta,
  emitFocusQueueChanged,
  subscribeToFocusQueueChanges
} from '../../utils/focusQueueEvents';

function TodayQueueWidget({
  title = 'Today',
  subtitle = 'Top focus items already waiting in your queue.',
  typeFilter = '',
  limit = 5,
  onNavigate
}) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [dismissingKey, setDismissingKey] = useState('');

  const loadQueue = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const payload = await listFocusQueue({ type: typeFilter, limit });
      setItems(Array.isArray(payload?.items) ? payload.items : []);
      setCounts(payload?.counts || null);
    } catch (err) {
      setError(err.message || 'Unable to load your focus queue.');
    } finally {
      setIsLoading(false);
    }
  }, [limit, typeFilter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => subscribeToFocusQueueChanges((event) => {
    const { action, item } = event.detail || {};
    const filterTypes = String(typeFilter || '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    const itemMatchesFilter = !filterTypes.length || filterTypes.includes(item?.type);
    if (action === 'dismiss' && item?.itemKey) {
      if (!itemMatchesFilter) return;
      setItems(current => current.filter(row => row.itemKey !== item.itemKey));
      setCounts(current => applyFocusQueueCountDelta(current, item, -1));
      return;
    }
    if (action === 'restore' && item?.itemKey) {
      if (!itemMatchesFilter) return;
      setCounts(current => applyFocusQueueCountDelta(current, item, 1));
      return;
    }
    if (action === 'refresh') {
      loadQueue();
    }
  }), [loadQueue, typeFilter]);

  const handleDismiss = async (item) => {
    if (!item?.itemKey) return;
    setDismissingKey(item.itemKey);
    emitFocusQueueChanged({ action: 'dismiss', item });
    try {
      await dismissFocusQueueItem(item.itemKey);
      emitFocusQueueChanged({ action: 'refresh', item });
    } catch (err) {
      emitFocusQueueChanged({ action: 'restore', item });
      setError(err.message || 'Unable to dismiss that item.');
    } finally {
      setDismissingKey('');
    }
  };

  return (
    <section className="today-queue-widget" aria-label={title}>
      <div className="today-queue-widget__head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <div className="today-queue-widget__count">
          <strong>{counts?.total ?? '—'}</strong>
          <span>{counts?.overdue ? `${counts.overdue} overdue` : 'in queue'}</span>
        </div>
      </div>

      {error ? <StatusBanner kind="error" message={error} onRetry={loadQueue} retryLabel="Try again" /> : null}

      {isLoading ? (
        <div className="today-queue-widget__list">
          {Array.from({ length: 3 }).map((_, index) => (
            <div className="today-queue-widget__skeleton" key={index}>
              <SkeletonBlock width={18} height={18} radius="var(--radius-pill)" />
              <div>
                <SkeletonBlock width="35%" height={12} />
                <SkeletonBlock width="80%" height={16} />
              </div>
            </div>
          ))}
        </div>
      ) : items.length ? (
        <div className="today-queue-widget__list">
          {items.map(item => (
            <FocusQueueRow
              key={item.itemKey}
              item={item}
              onCta={(path) => onNavigate?.(path)}
              onDismiss={handleDismiss}
              dismissing={dismissingKey === item.itemKey}
            />
          ))}
        </div>
      ) : !error ? (
        <EmptyState
          icon="✓"
          headline="Your queue is clear"
          body="No action items in flight. Nothing needs your attention right now."
          compact
        />
      ) : null}

      <div className="today-queue-widget__footer">
        <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/today')}>
          View all
        </Button>
      </div>
    </section>
  );
}

export default TodayQueueWidget;
